import { useRef, useMemo } from "react";
import { OptionInput } from "../../Inputs/OptionInput";
import { useTranslations } from "next-intl";
import { OptionChip, isRenderableOptionColor } from "@/lib/utils/optionChipColor";
import { isDanglingDynamicId, isUnresolvedWhilePending } from "./danglingDynamicId";
import { defaultChartColors } from "@/pages/event/dashboard/components/Tables/sliceColors";

type TableCellContentSingleSelectProps = {
  isFilter?: boolean;
  border?: string;
  editable: boolean;
  /** Class for the read-only value; the table leaves it unset and inherits. */
  readTextClass?: string;
  additionalField: boolean;
  options: string[];
  optionColors?: Record<string, string>;
  /** Dynamic id→name display map for optionSource fields (offering/room/etc.).
   *  When present, options display by name while the stored value stays the id. */
  dynamicLabels?: Record<string, string>;
  /**
   * Per-field i18n namespace for the option keys, instead of the shared
   * `optionsMultiSingleSelect` catalogue. See `Field.optionNamespace`.
   *
   * Carried here so the pointer is a FIELD-level contract rather than an opt-in
   * of one branch: `TableCellContent` used to forward it only to the
   * SINGLE_SELECT filter multi-pick, so the same field rendered elsewhere
   * silently reverted to the shared namespace.
   */
  optionNamespace?: string;
  /** The optionSource fetch is still in flight, so an id missing from the
   *  catalog means "not loaded yet", not "referenced thing was deleted". */
  optionsPending?: boolean;
  required: boolean;
  /** PB-RP-1: `undefined` = UNTOUCHED, `""` = CLEARED. The two are not the
   *  same and the component's own selection logic depends on telling them
   *  apart — see the `EMPTY_DISPLAY` branch below. */
  value: string | undefined;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  onFocusAction?: () => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  viewOnly?: boolean;
  fieldName?: string;
  testId?: string;
  /** Raw option values that are full (per-option capacity) — greyed out in the
   *  picker unless it's the current value. Never applied in filter mode. */
  disabledOptions?: string[];
};

export const TableCellContentSingleSelect: React.FC<
  TableCellContentSingleSelectProps
> = ({
  isFilter,
  border,
  editable,
  readTextClass,
  additionalField,
  options,
  optionColors,
  dynamicLabels,
  optionNamespace,
  optionsPending,
  required,
  value: rawValue,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  error,
  height,
  surface,
  fontSize,
  padding,
  rounded,
  viewOnly,
  fieldName,
  testId,
  disabledOptions,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("optionsMultiSingleSelect");
  // Root translator, so a per-field namespace resolves by dotted path;
  // `useTranslations` is a hook and cannot take a per-field argument.
  const tRoot = useTranslations();
  const isLanguageField = fieldName === "language";
  const isTimeZoneField = fieldName === "timeZone";
  const isRawValueField = isLanguageField || isTimeZoneField;
  // guestType is always set (mainGuest | companion) — a guest is never "neither".
  // Don't offer the "—" (is-empty) option in filter mode; it can only match zero
  // rows and reads as a meaningless third choice.
  const isAlwaysSetField = fieldName === "guestType";

  // Non-required selects (e.g. gender) AND every filter row expose a "—" not-set
  // option. Edit: clears the value. Filter: "—" + "=" is translated to the "∅"
  // (is empty) operator at serialize time (see coerceEmptyEqualsOperator in
  // parser.ts), so it matches null/unset rows instead of a literal equals-"".
  // Raw-value fields (language/timeZone) stay excluded — their
  // getKeyFromDisplayValue early-returns the display string, so "—" would leak
  // as a literal value rather than mapping back to "".
  //
  // The option's *value* is the em dash "—" itself (a real, non-empty string),
  // NOT "". Using "" would collide with OptionInput's empty `inputValue`: its
  // `value === inputValue` guard would dead-click the option whenever the field
  // is currently unset (the common case in bulk-edit). "—" maps back to "" only
  // at getKeyFromDisplayValue, so the parent still receives "" for a clear.
  const showEmptyOption = (isFilter || !required) && !isRawValueField && !isAlwaysSetField;
  const EMPTY_DISPLAY = "—";

  // Create a proper Map with original options as keys and translated/original values as values
  const optionsMap = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((option) => {
      // A FUNCTION, not a const — hoisting it makes the lookup eager and fires
      // a thrown-and-swallowed miss for every option the branches below never
      // ask about. `tRoot.has` first, so an out-of-catalogue value falls back
      // to the bare string rather than a dotted key.
      const translate = () => {
        if (optionNamespace === undefined) return t(option);
        const key = `${optionNamespace}.${option}`;
        return tRoot.has(key) ? tRoot(key) : option;
      };
      const displayValue =
        dynamicLabels?.[option] ??
        (additionalField || isTimeZoneField ? option : translate());
      map.set(option, displayValue);
    });
    return map;
  }, [options, additionalField, isTimeZoneField, t, tRoot, optionNamespace, dynamicLabels]);

  // Convert the Map values to an array for the OptionInput
  const displayOptions = useMemo(() => {
    const base = isRawValueField ? options : Array.from(optionsMap.values());
    // Don't double-add if a (custom) option's display already is "—".
    return showEmptyOption && !base.includes(EMPTY_DISPLAY)
      ? [...base, EMPTY_DISPLAY]
      : base;
  }, [optionsMap, isRawValueField, options, showEmptyOption]);

  // For language field: labels map so dropdown shows full names and search works on both
  const optionLabels = useMemo(() => {
    if (!isRawValueField) return undefined;
    const labels: Record<string, string> = {};
    optionsMap.forEach((displayValue, key) => {
      labels[key] = displayValue;
    });
    return labels;
  }, [isRawValueField, optionsMap]);

  // PB-RP-1: `rawValue` keeps the UNTOUCHED (undefined) vs CLEARED ("")
  // distinction the sentinel branch below depends on; everything downstream of
  // it wants a plain string and gets one. Collapsing the two at the boundary
  // is what made "—" a dead click on a never-set field.
  const value = rawValue ?? "";

  // Find the display value for the current value. A cleared value ("") shows the
  // "—" option as selected; an untouched value (undefined) shows nothing yet so
  // bulk-edit's "pick something first" gate still holds.
  const currentDisplayValue = useMemo(() => {
    if (isRawValueField) return value || "";
    if (showEmptyOption && rawValue === "") return EMPTY_DISPLAY;
    // BD-5 J4: surface an ORPHAN value (one no longer in `options` — e.g. an
    // option removed after the value was stored) instead of blanking it. The old
    // `optionsMap.get(value) || ""` resolved a removed value to "", so the inline
    // editor opened EMPTY over a stored value and any pick silently overwrote it
    // (invisible data loss). Fall back to the raw value so the editor shows what
    // is actually stored; free-commit (strictOption={!additionalField}) keeps it
    // editable and getKeyFromDisplayValue round-trips the raw value on save.
    return optionsMap.get(value) || value || "";
  }, [optionsMap, value, rawValue, isRawValueField, showEmptyOption]);

  // Function to get the original key from a display value
  const getKeyFromDisplayValue = (displayValue: string): string => {
    if (isRawValueField) return displayValue;
    // Real options win — so a (custom) option literally labelled "—" still maps
    // to its own key rather than being swallowed by the clear sentinel.
    for (const [key, val] of optionsMap.entries()) {
      if (val === displayValue) {
        return key;
      }
    }
    if (showEmptyOption && displayValue === EMPTY_DISPLAY) return "";
    return displayValue; // Fallback to the display value if no key is found
  };

  // Effective per-option colors: for custom (additionalField) SELECTs, fall back
  // to the same default chart palette the field editor swatches and dashboard
  // charts use — so a real option always shows a colored chip even when the user
  // never explicitly picked a color. Explicit hex colors win over the default.
  // Built-in selects (language/timeZone/gender) opt out: they keep their plain
  // rendering and only color from an explicit map.
  const effectiveColors = useMemo(
    () =>
      additionalField
        ? { ...defaultChartColors(options), ...(optionColors ?? {}) }
        : optionColors ?? {},
    [additionalField, options, optionColors],
  );

  // Read-mode rendering: a colored chip when the selected option carries a
  // color, otherwise the plain text it has always been. Stale values that are no
  // longer in `options` have no entry in `effectiveColors`, so they stay plain.
  //
  // This is a PURE DISPLAY path — unlike `currentDisplayValue` above it never
  // feeds getKeyFromDisplayValue, so masking here cannot round-trip a label back
  // into the stored value. That is exactly why the masking lives here and not
  // there: substituting a placeholder on the editor path would let the next pick
  // save the placeholder string, and would collapse two dangling ids into one.
  const readDisplayValue = useMemo(() => {
    // Catalog still loading: blank, not "deleted". `dyn` is always truthy in
    // FieldsProvider, so a mid-fetch field carries `options: []` and every id
    // would otherwise look dead on every single mount.
    if (isUnresolvedWhilePending(value, optionsMap, dynamicLabels, optionsPending)) return "";
    if (isDanglingDynamicId(value, optionsMap, dynamicLabels)) {
      // Keep the id tail: two different dead references must stay
      // distinguishable, and support can still trace the row.
      return t("deletedReferenceLabel", { idTail: value.slice(-4) });
    }
    return optionsMap.get(value) || value;
  }, [optionsMap, value, dynamicLabels, optionsPending, t]);
  const readContent =
    value && isRenderableOptionColor(effectiveColors[value]) ? (
      <OptionChip
        label={readDisplayValue}
        color={effectiveColors[value]}
        className="max-w-full"
      />
    ) : (
      <p className={`truncate ${readTextClass ?? ""}`}>{readDisplayValue}</p>
    );

  return (
    <>
      {!editable && !isFilter ? (
        readContent
      ) : isEditing && isSelected ? (
        <div className="flex flex-col flex-1">
          <OptionInput
            height={height}
            surface={surface}
            fontSize={fontSize}
            padding={padding}
            disabled={viewOnly}
            rounded={rounded}
            isFilter={isFilter}
            onFocusAction={onFocusAction}
            border={border}
            inputRef={inputRef}
            setParent={(selectedDisplayValue) => {
              // Convert the selected display value back to the original key
              const originalKey = getKeyFromDisplayValue(selectedDisplayValue);
              onChangeValue(originalKey);
            }}
            options={displayOptions}
            defaultValue={currentDisplayValue}
            required={required}
            optionLabels={optionLabels}
            optionColors={effectiveColors}
            // Full options greyed out (mapped raw→display keys). Never in filters.
            disabledOptions={
              isFilter
                ? undefined
                : disabledOptions?.map((k) => optionsMap.get(k) ?? k)
            }
            // Alphabetize user-defined option lists and the raw-value fields
            // (language/timeZone — "also the translated ones": sorts by the
            // shown name, not the code). Built-in semantic enums (status
            // funnel, gender) keep their deliberate order.
            sortOptions={additionalField || isRawValueField}
            // Built-in fixed selects (language/timeZone/gender/status…) must
            // hold a real option — typing only filters, blur snaps back to the
            // last valid value. Custom (additionalField) selects keep their
            // existing free-commit behavior.
            strictOption={!additionalField}
            testId={testId}
          />
          {error && <p className="mt-1 body-3-regular opacity-0">{error}</p>}
        </div>
      ) : (
        readContent
      )}
    </>
  );
};
